import React from 'react';
import { Store } from 'lucide-react';

export interface CommerceFormDetailsSectionProps {
  styles: Record<string, string>;
  disabled: boolean;
  isFieldInvalid: (fieldId: string) => boolean;
  logoInputRef: React.RefObject<HTMLInputElement>;
  logo: File | null;
  setLogo: (f: File | null) => void;
  logoCircleSrc: string | null;
  prefillLogoUrl?: string;
  name: string;
  setName: (v: string) => void;
  delivery: boolean;
  setDelivery: (v: boolean) => void;
  urlSite: string;
  setUrlSite: (v: string) => void;
  deliveryCondition: string;
  setDeliveryCondition: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  children: React.ReactNode;
}

const CommerceFormDetailsSection: React.FC<CommerceFormDetailsSectionProps> = ({
  styles,
  disabled,
  isFieldInvalid,
  logoInputRef,
  logo,
  setLogo,
  logoCircleSrc,
  prefillLogoUrl,
  name,
  setName,
  delivery,
  setDelivery,
  urlSite,
  setUrlSite,
  deliveryCondition,
  setDeliveryCondition,
  description,
  setDescription,
  children,
}) => {
  return (
    <section className={styles.detailsSection} aria-labelledby="commerce-details-heading">
      <h3 id="commerce-details-heading" className={styles.detailsTitle}>
        Datos del comercio
      </h3>
      <p className={styles.detailsHint}>
        Los datos se guardan como comercio en Edify. Podés ajustar nombre y descripción si tu tienda se llama distinto
        a tu perfil.
      </p>
      <div className={styles.commercePresentation}>
        <div className={styles.presentationLogo}>
          <span
            className={`${styles.presentationLogoLabel} ${isFieldInvalid('commerce-logo-pick') ? styles.fieldInvalidLabel : ''}`}
          >
            Logo <span className={styles.presentationLogoHint}>(obligatorio · JPG o PNG)</span>
          </span>
          <div className={styles.logoPicker}>
            <input
              ref={logoInputRef}
              id="commerce-logo"
              type="file"
              accept="image/jpeg,image/png"
              className={styles.presentationLogoInputHidden}
              disabled={disabled}
              onChange={(e) => {
                const f = e.target.files?.[0];
                setLogo(f ?? null);
              }}
            />
            <button
              id="commerce-logo-pick"
              type="button"
              className={`${styles.logoAvatarPick} ${isFieldInvalid('commerce-logo-pick') ? styles.logoAvatarPickInvalid : ''}`}
              disabled={disabled}
              onClick={() => logoInputRef.current?.click()}
              aria-label={
                logo
                  ? `Logo: ${logo.name}. Tocá para cambiar la imagen`
                  : prefillLogoUrl?.trim()
                    ? 'Logo del comercio. Tocá para cambiar la imagen'
                    : 'Elegir logo del comercio, JPG o PNG'
              }
            >
              {logoCircleSrc ? (
                <img src={logoCircleSrc} alt="" className={styles.logoAvatarImg} />
              ) : (
                <span className={styles.logoAvatarPlaceholder} aria-hidden>
                  <Store strokeWidth={1.75} />
                </span>
              )}
            </button>
            {isFieldInvalid('commerce-logo-pick') ? (
              <span className={styles.fieldInvalidHint}>Subí una imagen para el logo.</span>
            ) : null}
          </div>
        </div>
        <div className={styles.presentationStack}>
          <div className={styles.field}>
            <label
              className={`${styles.label} ${isFieldInvalid('commerce-name') ? styles.fieldInvalidLabel : ''}`}
              htmlFor="commerce-name"
            >
              Nombre del comercio
            </label>
            <input
              id="commerce-name"
              className={`${styles.inputMuted} ${isFieldInvalid('commerce-name') ? styles.inputMutedInvalid : ''}`}
              value={name}
              disabled={disabled}
              onChange={(e) => setName(e.target.value)}
              autoComplete="organization"
              aria-invalid={isFieldInvalid('commerce-name')}
            />
            {isFieldInvalid('commerce-name') ? (
              <span className={styles.fieldInvalidHint}>Completá este dato</span>
            ) : null}
          </div>
          <div className={styles.presentationMetaRow}>
            <div className={styles.field}>
              <span className={styles.label}>¿Hacés envíos?</span>
              <div className={styles.rowInline}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${delivery ? styles.toggleActive : ''}`}
                  disabled={disabled}
                  onClick={() => setDelivery(true)}
                >
                  Sí
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${!delivery ? styles.toggleActive : ''}`}
                  disabled={disabled}
                  onClick={() => setDelivery(false)}
                >
                  No
                </button>
              </div>
            </div>
            <div className={`${styles.field} ${styles.presentationUrlField}`}>
              <label className={styles.label} htmlFor="commerce-url">
                Sitio web (opcional)
              </label>
              <input
                id="commerce-url"
                className={styles.inputMuted}
                type="url"
                value={urlSite}
                disabled={disabled}
                onChange={(e) => setUrlSite(e.target.value)}
              />
            </div>
          </div>
          {delivery ? (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="commerce-del-cond">
                Condiciones de envío (opcional)
              </label>
              <input
                id="commerce-del-cond"
                className={styles.inputMuted}
                value={deliveryCondition}
                disabled={disabled}
                onChange={(e) => setDeliveryCondition(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.field}>
        <label
          className={`${styles.label} ${isFieldInvalid('commerce-desc') ? styles.fieldInvalidLabel : ''}`}
          htmlFor="commerce-desc"
        >
          Descripción
        </label>
        <textarea
          id="commerce-desc"
          className={`${styles.textareaMuted} ${isFieldInvalid('commerce-desc') ? styles.textareaMutedInvalid : ''}`}
          value={description}
          disabled={disabled}
          onChange={(e) => setDescription(e.target.value)}
          aria-invalid={isFieldInvalid('commerce-desc')}
        />
        {isFieldInvalid('commerce-desc') ? (
          <span className={styles.fieldInvalidHint}>Completá este dato</span>
        ) : null}
      </div>
      {children}
    </section>
  );
};

export default CommerceFormDetailsSection;
